import { FC, memo, useCallback, useEffect } from 'react'
import { FileInput } from 'components/features/pfm/FileInput/FileInput'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { cropImageActionCreator, clearCropperActionCreator } from 'actions/cropperActions'
import { cropperOutputSelector } from 'selectors/cropperSelectors'
import { isImageGifType, pickFirstImage } from 'helpers/DOMHelpers'

type CropperFileInputProps = {
  onCrop: (file: Blob) => void
  onError: () => void
}

export const CropperFileInput: FC<CropperFileInputProps> = memo(({
  children,
  onCrop,
  onError,
}) => {
  const dispatch = useStoreDispatch()
  const outputImage = useStoreSelector(cropperOutputSelector)

  const handleChange = useCallback<(files: File[]) => void>(async (files) => {
    const { image, isError } = await pickFirstImage(files)
    if (isError) {
      onError && onError()
      return
    }

    if (!image) {
      return
    }

    if (isImageGifType(image.type)) {
      onCrop(image)
      return
    }

    dispatch(cropImageActionCreator({
      image,
    }))
  }, [dispatch, onCrop, onError])

  useEffect(() => {
    if (outputImage) {
      dispatch(clearCropperActionCreator())
      onCrop(outputImage)
    }
  }, [dispatch, onCrop, outputImage])

  return (
    <FileInput
      onChange={ handleChange }
      images
    >
      { children }
    </FileInput>
  )
})
